import './navigation-menu.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { SideNavToolbarProps, MenuOpenState, MenuStatus } from '../../types';
import { useNavigate } from 'react-router-dom';
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';
import { useMenuPatch } from '../../utils/patches';
import { ButtonTypes } from 'devextreme-react/cjs/button';
import { TreeViewTypes } from 'devextreme-react/cjs/tree-view';
import { NavigationItem, NavigationTemplate } from '../../layouts/navigation-template/navigation-template';
import Header from '../header/Header';
import Footer from '../footer/Footer';
import { Drawer, Template } from 'devextreme-react';

export const NavigationMenu = ({ title, children }: React.PropsWithChildren<SideNavToolbarProps>) => {
    const navigate = useNavigate();
    const { isXSmall, isLarge } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();
    const [patchCssClass, onMenuReady] = useMenuPatch();
    const [menuStatus, setMenuStatus] = useState<MenuStatus>(null);

    const getDefaultMenuOpenState = useCallback(() => isLarge ? MenuOpenState.Opened : MenuOpenState.Closed, [isLarge]);
    const getMenuOpenState = useCallback((status: MenuStatus) => {
        if (status === null)
            return getDefaultMenuOpenState();

        return status;
    }, [getDefaultMenuOpenState]);

    const getMenuStatus = useCallback((status: MenuStatus) => {
        return status === getDefaultMenuOpenState() ? null : status;
    }, [getDefaultMenuOpenState]);

    const changeMenuStatus = useCallback((reducerFn: (prevStatus: MenuStatus) => MenuStatus) => {
        setMenuStatus(prevMenuStatus => getMenuStatus(reducerFn(getMenuOpenState(prevMenuStatus)) ?? prevMenuStatus));
    }, [getMenuOpenState, getMenuStatus]);

    const toggleMenu = useCallback(({ event }: ButtonTypes.ClickEvent) => {
        changeMenuStatus(prevStatus => prevStatus === MenuOpenState.Closed ? MenuOpenState.Opened : MenuOpenState.Closed);
        event?.stopPropagation();
    }, [changeMenuStatus]);

    const temporaryOpenMenu = useCallback(() => {
        changeMenuStatus(prevStatus => prevStatus === MenuOpenState.Closed ? MenuOpenState.TemporaryOpened : null);
    }, [changeMenuStatus]);

    const onOutsideClick = useCallback(() => {
        changeMenuStatus(prevStatus => prevStatus !== MenuOpenState.Closed && !isLarge ? MenuOpenState.Closed : null);
        return !isLarge;
    }, [isLarge, changeMenuStatus]);

    const onNavigationChanged = useCallback(
        ({ itemData: { path }, event, node }: TreeViewTypes.ItemClickEvent & { itemData: NavigationItem }) => {
            event?.stopPropagation();

            if (!path || node?.selected)
                return;

            navigate(path);

            if (menuStatus === MenuOpenState.Closed) {
                setMenuStatus(getMenuStatus(MenuOpenState.Opened));
                return;
            }

            if (!isLarge || menuStatus === MenuOpenState.TemporaryOpened)
                setMenuStatus(getMenuStatus(MenuOpenState.Closed));
        },
        [navigate, menuStatus, isLarge]
    ) as (e: TreeViewTypes.ItemClickEvent) => void;

    useEffect(() => {
        changeMenuStatus(() => menuStatus);
    }, [isLarge]);

    return (
        <div className='side-nav-inner-toolbar'>
            <Header className={screenSizeClass} menuToggleEnabled toggleMenu={toggleMenu} title={title} />
            <Drawer
                className={['drawer layout-body', patchCssClass].join(' ')}
                position='before'
                closeOnOutsideClick={onOutsideClick}
                openedStateMode={isLarge ? 'shrink' : 'overlap'}
                revealMode={isXSmall ? 'slide' : 'expand'}
                minSize={isXSmall ? 0 : 50}
                maxSize={270}
                shading={isLarge ? false : true}
                opened={getMenuOpenState(menuStatus) === MenuOpenState.Closed ? false : true}
                template='menu'
            >
                <div className='content'>
                    {React.Children.map(children, (item) => {
                        return React.isValidElement(item) && item.type !== Footer && item;
                    })}
                </div>

                <Template name='menu'>
                    <NavigationTemplate
                        compactMode={getMenuOpenState(menuStatus) === MenuOpenState.Closed}
                        selectedItemChanged={onNavigationChanged}
                        openMenu={temporaryOpenMenu}
                        onMenuReady={onMenuReady}
                        menuStatus={getMenuOpenState(menuStatus)}
                    />
                </Template>
            </Drawer>
            {/* <Footer>
                Copyright © 2025 by{" "}
                <a
                    style={{ textDecoration: "none" }}
                    href="https://www.pragmos.it"
                    target="_blank"
                >
                    Pragmos
                </a>
            </Footer> */}
        </div>
    )
}